import { useMemo } from "react";
import { sttTitle } from "./stt";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { formatDate, formatTime, isoDate } from "../../shared/format";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import type {
  AppointmentStatus,
  AppointmentSummary,
} from "../../shared/types/appointment";
import { PageHeader } from "../../shared/ui/PageHeader";
import { PatientLink } from "../../shared/ui/PatientLink";
import { ErrorState } from "../../shared/ui/ErrorState";
import { SkeletonRows } from "../../shared/ui/Skeleton";
import { EmptyState } from "../../shared/ui/EmptyState";
import { useKeyboardShortcut } from "../../shared/ui/useKeyboardShortcut";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const COLUMNS: { status: AppointmentStatus; label: string }[] = [
  { status: "REQUESTED", label: "Chờ xác nhận" },
  { status: "CONFIRMED", label: "Đã xác nhận" },
  { status: "CHECKED_IN", label: "Đã nhận khám" },
  { status: "COMPLETED", label: "Đã khám xong" },
];

export function AppointmentDayPage() {
  const navigate = useNavigate();
  // Ngày + bác sĩ đang xem nằm trên URL (mặc định hôm nay), để reload / gửi link mở đúng ngày.
  const { filters, set } = useUrlFilters({
    date: param.isoDate(() => isoDate(new Date())),
    doctorId: param.string(),
  });
  const { date, doctorId } = filters;
  const setDate = (d: string) => set({ date: d });

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const day = useQuery({
    queryKey: appointmentQueryKeys.forDate(date),
    queryFn: () => appointmentsApi.forDate(date),
  });

  const filtered = useMemo(() => {
    if (!day.data) return [];
    if (!doctorId) return day.data;
    return day.data.filter((a) => a.doctorId === doctorId);
  }, [day.data, doctorId]);

  const grouped = useMemo(() => {
    const out: Record<AppointmentStatus, AppointmentSummary[]> = {
      REQUESTED: [],
      CONFIRMED: [],
      CHECKED_IN: [],
      COMPLETED: [],
      NO_SHOW: [],
      CANCELLED: [],
    };
    for (const a of filtered) out[a.status].push(a);
    return out;
  }, [filtered]);

  const shiftDay = (offset: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(isoDate(d));
  };

  // ---- Keyboard shortcuts (M27) ------------------------------------
  useKeyboardShortcut({ key: "arrowleft" }, () => shiftDay(-1), [date]);
  useKeyboardShortcut({ key: "arrowright" }, () => shiftDay(1), [date]);
  useKeyboardShortcut({ key: "t" }, () => setDate(isoDate(new Date())), []);
  useKeyboardShortcut({ key: "n" }, () => navigate("/appointments/new"), [navigate]);
  useKeyboardShortcut({ key: "w" }, () => navigate("/appointments"), [navigate]);

  return (
    <div>
      <PageHeader
        title="Lịch hẹn theo ngày"
        description={
          <>
            Bảng kanban theo trạng thái cho <strong>{formatDate(date)}</strong>.
            Lễ tân kéo bệnh nhân qua các cột khi tiếp đón.
          </>
        }
        breadcrumbs={[
          { label: "Trang chủ", to: "/dashboard" },
          { label: "Lịch hẹn", to: "/appointments" },
          { label: "Theo ngày" },
        ]}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/appointments">Lịch tuần</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/appointments/list">Danh sách</Link>
            </Button>
            <Button asChild>
              <Link to="/appointments/new">
                <Plus />
                Đặt lịch
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftDay(-1)}
          aria-label="Ngày trước"
        >
          <ChevronLeft />
        </Button>
        <Input
          type="date"
          className="w-auto"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Chọn ngày"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftDay(1)}
          aria-label="Ngày sau"
        >
          <ChevronRight />
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setDate(isoDate(new Date()))}
        >
          Hôm nay
        </Button>
        <select
          className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={doctorId}
          onChange={(e) => set({ doctorId: e.target.value })}
          aria-label="Lọc bác sĩ"
        >
          <option value="">Mọi bác sĩ</option>
          {doctors.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <span className="ml-auto text-sm text-muted-foreground">
          {filtered.length} lịch hẹn
        </span>
      </div>

      {day.isError && <ErrorState error={day.error} inline />}
      {day.isLoading && <SkeletonRows rows={5} cols={4} />}
      {!day.isLoading && !day.isError && filtered.length === 0 && (
        <EmptyState
          title="Không có lịch hẹn nào trong ngày này"
          description="Thử đổi sang ngày khác hoặc đặt lịch mới cho bệnh nhân."
          actions={
            <Button asChild>
              <Link to="/appointments/new">
                <Plus />
                Đặt lịch
              </Link>
            </Button>
          }
        />
      )}
      {!day.isLoading && !day.isError && filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {COLUMNS.map(({ status, label }) => (
            <DayColumn
              key={status}
              label={label}
              status={status}
              items={grouped[status]}
              onOpen={(a) => navigate(`/appointments/${a.id}`)}
            />
          ))}
          {grouped.NO_SHOW.length + grouped.CANCELLED.length > 0 && (
            <DayColumn
              label="Khác"
              status="CANCELLED"
              items={[...grouped.NO_SHOW, ...grouped.CANCELLED]}
              onOpen={(a) => navigate(`/appointments/${a.id}`)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DayColumn({
  label,
  status,
  items,
  onOpen,
}: {
  label: string;
  status: AppointmentStatus;
  items: AppointmentSummary[];
  onOpen: (a: AppointmentSummary) => void;
}) {
  return (
    <div className="flex flex-col gap-3" aria-label={label} data-status={status}>
      <div className="flex items-center justify-between border-b border-border pb-2">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <Badge variant="muted">{items.length}</Badge>
      </div>
      {items.length === 0 && (
        <p className="m-0 text-xs text-muted-foreground">Trống</p>
      )}
      {items.map((a) => (
        <Card
          key={a.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(a)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onOpen(a);
          }}
          className="cursor-pointer space-y-1 p-3 transition-colors hover:border-primary/50 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-primary">{formatTime(a.startAt)}</span>
            {/* #19552 — STT (roomNo, #19934), cùng số với Chi tiết lịch hẹn; "—" khi chưa có. */}
            <Badge
              variant="outline"
              className="shrink-0 tabular-nums"
              data-testid="day-card-booking-no"
              title="STT đặt lịch (theo Phạm vi STT)"
            >
              {sttTitle(a)}
            </Badge>
          </div>
          <PatientLink
            id={a.patientId}
            name={a.patientName}
            className="text-sm font-medium text-foreground"
          />
          <div className="text-xs text-muted-foreground">{a.doctorName}</div>
          {a.serviceName && (
            <div className="text-xs text-muted-foreground">
              {a.serviceName} · {a.durationMinutes}p
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
